import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { useLinkedInTokenStatus } from '@/hooks';
import { useFacebookTokenStatus } from '@/hooks/useFacebookTokenStatus';
import { useTwitterTokenStatus } from '@/hooks/useTwitterTokenStatus';

interface InstagramAccount {
    instagramAccountId: string;
    username: string;
    name: string;
    profilePictureUrl?: string;
    linkedPageId: string;
    linkedPageName: string;
    pageAccessToken: string;
}

interface FacebookPage {
    pageId: string;
    pageName: string;
    pageAccessToken: string;
    category?: string;
}

/**
 * Hook to manage all social connection states
 * Single source of truth for LinkedIn, Facebook, and Instagram connections
 */
export const useSocialConnections = () => {
    // LinkedIn status from existing hook
    const {
        hasToken: linkedInConnected,
        isLoading: linkedInStatusLoading,
        refetch: refetchLinkedIn,
    } = useLinkedInTokenStatus();

    // Facebook/Instagram status from existing hook
    const {
        hasToken: facebookConnected,
        isLoading: facebookStatusLoading,
        refetch: refetchFacebook,
        selectedPageName: facebookSelectedPageName,
        selectedInstagramAccountId: facebookSelectedInstagramAccountId,
    } = useFacebookTokenStatus();

    // Twitter status from existing hook
    const {
        hasToken: twitterConnected,
        isLoading: twitterStatusLoading,
        refetch: refetchTwitter,
        username: twitterUsername,
    } = useTwitterTokenStatus();

    // Local state for selections and lists
    const [linkedInCompanyPages] = useState<Record<string, unknown>[]>([]);
    const [facebookPages, setFacebookPages] = useState<FacebookPage[]>([]);
    const [instagramAccounts, setInstagramAccounts] = useState<InstagramAccount[]>([]);
    const [selectedFacebookPage, setSelectedFacebookPage] = useState<FacebookPage | null>(null);
    const [selectedInstagramAccount, setSelectedInstagramAccount] = useState<InstagramAccount | null>(null);
    const [isConnecting, setIsConnecting] = useState<'linkedin' | 'facebook' | 'instagram' | 'twitter' | null>(null);
    const [pagesLoading, setPagesLoading] = useState(false);
    const [accountsLoading, setAccountsLoading] = useState(false);

    // Fetch Facebook pages and Instagram accounts when connected
    useEffect(() => {
        const fetchData = async () => {
            if (!facebookConnected) {
                setFacebookPages([]);
                setInstagramAccounts([]);
                return;
            }

            setPagesLoading(true);
            setAccountsLoading(true);

            try {
                // Fetch Facebook pages
                const pagesResponse = await api.get('/users/facebook-pages');
                setFacebookPages(pagesResponse.data.pages || []);
            } catch (error) {
                console.error('Failed to fetch Facebook pages:', error);
            } finally {
                setPagesLoading(false);
            }

            try {
                // Fetch Instagram accounts
                const accountsResponse = await api.get('/users/instagram-accounts');
                setInstagramAccounts(accountsResponse.data.accounts || []);
            } catch (error) {
                console.error('Failed to fetch Instagram accounts:', error);
            } finally {
                setAccountsLoading(false);
            }
        };

        fetchData();
    }, [facebookConnected]);

    // Sync selected page/account from status
    useEffect(() => {
        if (facebookPages.length > 0 && facebookSelectedPageName) {
            const page = facebookPages.find(p => p.pageName === facebookSelectedPageName);
            if (page) setSelectedFacebookPage(page);
        }

        // Sync Instagram selection from status
        if (instagramAccounts.length > 0 && facebookSelectedInstagramAccountId) {
            const account = instagramAccounts.find(a => a.instagramAccountId === facebookSelectedInstagramAccountId);
            if (account) setSelectedInstagramAccount(account);
        }
    }, [facebookPages, instagramAccounts, facebookSelectedPageName, facebookSelectedInstagramAccountId]);

    // Connect handlers
    const connectLinkedIn = useCallback(async (redirectUrl?: string) => {
        setIsConnecting('linkedin');
        try {
            const response = await api.post('/auth/linkedin-connect-posting', {
                redirectUrl: redirectUrl || window.location.pathname,
            });
            window.location.href = response.data.authUrl;
        } catch (error: unknown) {
            console.error('Failed to connect LinkedIn:', error);
            alert(getErrorMessage(error));
            setIsConnecting(null);
        }
    }, []);

    const connectFacebookInstagram = useCallback(async (redirectUrl?: string) => {
        setIsConnecting('facebook');
        try {
            const response = await api.post('/auth/facebook-connect-posting', {
                redirectUrl: redirectUrl || window.location.pathname,
            });
            window.location.href = response.data.authUrl;
        } catch (error: unknown) {
            console.error('Failed to connect Facebook/Instagram:', error);
            alert(getErrorMessage(error));
            setIsConnecting(null);
        }
    }, []);

    // Disconnect handlers
    const disconnectLinkedIn = useCallback(async () => {
        if (!confirm('Disconnect LinkedIn? You will need to reconnect to post.')) return;
        setIsConnecting('linkedin');
        try {
            await api.delete('/users/linkedin-disconnect');
            await refetchLinkedIn();
        } catch (error: unknown) {
            console.error('Failed to disconnect LinkedIn:', error);
            alert(getErrorMessage(error));
        } finally {
            setIsConnecting(null);
        }
    }, [refetchLinkedIn]);

    const disconnectFacebookInstagram = useCallback(async () => {
        if (!confirm('Disconnect Facebook/Instagram? You will need to reconnect to post.')) return;
        setIsConnecting('facebook');
        try {
            await api.delete('/users/facebook-disconnect');
            await refetchFacebook();
            setFacebookPages([]);
            setInstagramAccounts([]);
            setSelectedFacebookPage(null);
            setSelectedInstagramAccount(null);
        } catch (error: unknown) {
            console.error('Failed to disconnect:', error);
            alert(getErrorMessage(error));
        } finally {
            setIsConnecting(null);
        }
    }, [refetchFacebook]);

    const connectTwitter = useCallback(async (redirectUrl?: string) => {
        setIsConnecting('twitter');
        try {
            const response = await api.post('/auth/twitter-connect-posting', {
                redirectUrl: redirectUrl || window.location.pathname,
            });
            window.location.href = response.data.authUrl;
        } catch (error: unknown) {
            console.error('Failed to connect Twitter:', error);
            alert(getErrorMessage(error));
            setIsConnecting(null);
        }
    }, []);

    const disconnectTwitter = useCallback(async () => {
        if (!confirm('Disconnect Twitter? You will need to reconnect to post.')) return;
        setIsConnecting('twitter');
        try {
            await api.delete('/users/twitter-disconnect');
            await refetchTwitter();
        } catch (error: unknown) {
            console.error('Failed to disconnect Twitter:', error);
            alert(getErrorMessage(error));
        } finally {
            setIsConnecting(null);
        }
    }, [refetchTwitter]);

    // Selection handlers
    const selectFacebookPage = useCallback(async (page: FacebookPage | null) => {
        setSelectedFacebookPage(page);
        if (page) {
            try {
                await api.post('/users/facebook-page', {
                    pageId: page.pageId,
                    pageName: page.pageName,
                    pageAccessToken: page.pageAccessToken,
                });
                await refetchFacebook(); // Refresh status to update UI
            } catch (error) {
                console.error('Failed to save Facebook page selection:', error);
            }
        }
    }, [refetchFacebook]);

    const selectInstagramAccount = useCallback(async (account: InstagramAccount | null) => {
        setSelectedInstagramAccount(account);
        if (account) {
            try {
                await api.post('/users/instagram-account', {
                    instagramAccountId: account.instagramAccountId,
                    username: account.username,
                    pageAccessToken: account.pageAccessToken,
                });
                await refetchFacebook(); // Refresh status to update UI
            } catch (error) {
                console.error('Failed to save Instagram account selection:', error);
            }
        }
    }, [refetchFacebook]);

    // Refetch all
    const refetchAll = useCallback(async () => {
        await Promise.all([refetchLinkedIn(), refetchFacebook(), refetchTwitter()]);
    }, [refetchLinkedIn, refetchFacebook, refetchTwitter]);

    return {
        // Connection states
        linkedInConnected,
        facebookConnected, // Also covers Instagram since they share OAuth
        instagramConnected: facebookConnected && instagramAccounts.length > 0,
        twitterConnected,

        // Loading states
        isLoading: linkedInStatusLoading || facebookStatusLoading || twitterStatusLoading,
        isConnecting,
        pagesLoading,
        accountsLoading,

        // Data
        linkedInCompanyPages,
        facebookPages,
        facebookSelectedPageName,
        instagramAccounts,
        selectedFacebookPage,
        selectedInstagramAccount,
        twitterUsername,

        // Actions
        connectLinkedIn,
        connectFacebookInstagram,
        connectTwitter,
        disconnectLinkedIn,
        disconnectFacebookInstagram,
        disconnectTwitter,
        selectFacebookPage,
        selectInstagramAccount,
        refetchAll,
    };
};
